import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  MenuItem,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  SwapHoriz as SwapHorizIcon,
  Send as SendIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { ethers } from 'ethers';
import { useAuth } from '../contexts/AuthContext';
import { useBot } from '../contexts/BotContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://run.phazeflow.com';

const Delegation = () => {
  const { token } = useAuth();
  const { isMonitoring, startMonitoring, stopMonitoring } = useBot();
  const [wallets, setWallets] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [actions, setActions] = useState([]);
  const [stakingContracts, setStakingContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingDelegation, setCreatingDelegation] = useState(false);
  const [revokingDelegation, setRevokingDelegation] = useState(false);
  const [directUnstaking, setDirectUnstaking] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createDelegationDialog, setCreateDelegationDialog] = useState(false);
  const [sponsorDialog, setSponsorDialog] = useState(false);
  const [stakingContractDialog, setStakingContractDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  // Form states
  const [delegationForm, setDelegationForm] = useState({
    walletId: '',
    chainKey: '',
  });

  const [sponsorForm, setSponsorForm] = useState({
    walletId: '',
    sponsorPrivateKey: ''
  });

  const [stakingContractForm, setStakingContractForm] = useState({
    chainKey: 'ethereum',
    contractAddress: '',
    contractName: '',
    tokenAddress: '',
    rewardTokenAddress: '',
    balanceMethod: '',
    customBalanceMethod: '',
    balanceReturnType: 'uint256',
    customBalanceReturnType: '',
    unstakeMethod: '',
    customUnstakeMethod: '',
    unstakeParams: [], // New state for unstake method parameters
    unstakeParamTypes: [] // New state for parameter types (amount, index, etc.)
  });

  // Direct Unstaking Form State
  const [directUnstakeDialog, setDirectUnstakeDialog] = useState(false);
  const [directUnstakeForm, setDirectUnstakeForm] = useState({
    walletId: '',
    chainKey: 'ethereum',
    contractAddress: '',
    unstakeMethod: '',
    customUnstakeMethod: '',
    unstakeAmount: '',
    unstakeParams: [],
    unstakeParamTypes: [],
    tokenAddress: '',
    recipientAddress: ''
  });

  // Relayer status
  const [relayerStatus, setRelayerStatus] = useState({
    isRunning: false,
    monitoredWallets: 0,
    providers: [],
    contracts: []
  });



  // 1. Add state for sponsorBalances
  const [sponsorBalances, setSponsorBalances] = useState([]);

  // --- User-level Sponsor Private Key Management Card ---
  const [userSponsorKey, setUserSponsorKey] = useState('');
  const [showUserSponsorKey, setShowUserSponsorKey] = useState(false);
  
  // --- Recipient Address Management ---
  const [recipientAddress, setRecipientAddress] = useState('');
  const [showRecipientAddress, setShowRecipientAddress] = useState(false);

  // Fetch sponsor key on page load
  useEffect(() => {
    const fetchUserSponsorKey = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) setUserSponsorKey(data.sponsorPrivateKey || '');
      } catch (error) {
        setUserSponsorKey('');
      }
    };
    fetchUserSponsorKey();
  }, [token]);

  // Fetch recipient address on page load
  useEffect(() => {
    const fetchRecipientAddress = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.recipientAddress) {
            setRecipientAddress(data.recipientAddress);
          }
        }
      } catch (error) {
        console.error('Failed to load recipient address:', error);
        setRecipientAddress('');
      }
    };
    fetchRecipientAddress();
  }, [token]);

  const handleSetUserSponsorKey = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sponsorPrivateKey: userSponsorKey })
      });
      const data = await response.json();
      if (data.success) setSuccess('Sponsor private key updated successfully');
      else setError(data.error || 'Failed to update sponsor key');
    } catch (error) {
      setError('Failed to update sponsor key');
    }
  };
  
  // Recipient address management functions
  const handleSetRecipientAddress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ recipientAddress: recipientAddress })
      });
      const data = await response.json();
      if (data.success) setSuccess('Recipient address updated successfully');
      else setError(data.error || 'Failed to update recipient address');
    } catch (error) {
      setError('Failed to update recipient address');
    }
  };
  
  const handleRemoveRecipientAddress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/recipient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ recipientAddress: '' })
      });
      const data = await response.json();
      if (data.success) {
        setRecipientAddress('');
        setSuccess('Recipient address removed successfully');
      } else setError(data.error || 'Failed to remove recipient address');
    } catch (error) {
      setError('Failed to remove recipient address');
    }
  };
  // --- End User-level Sponsor Private Key Management Card ---

  const commonUnstakeMethods = [
    { label: 'unstake(amount)', value: 'unstake_amount', params: ['uint256'], paramTypes: ['amount'] },
    { label: 'withdraw(amount)', value: 'withdraw_amount', params: ['uint256'], paramTypes: ['amount'] },
    { label: 'unstake(index)', value: 'unstake_index', params: ['uint256'], paramTypes: ['index'] },
    { label: 'withdraw(index)', value: 'withdraw_index', params: ['uint256'], paramTypes: ['index'] },
    { label: 'unstake(address,index)', value: 'unstake_address_index', params: ['address', 'uint256'], paramTypes: ['address', 'index'] },
    { label: 'withdraw(address,index)', value: 'withdraw_address_index', params: ['address', 'uint256'], paramTypes: ['address', 'index'] },
    { label: 'unstake(index,amount)', value: 'unstake_index_amount', params: ['uint256', 'uint256'], paramTypes: ['index', 'amount'] },
    { label: 'withdraw(index,amount)', value: 'withdraw_index_amount', params: ['uint256', 'uint256'], paramTypes: ['index', 'amount'] },
    { label: 'exit()', value: 'exit', params: [], paramTypes: [] },
    { label: 'emergencyWithdraw()', value: 'emergencyWithdraw', params: [], paramTypes: [] },
    { label: 'Custom...', value: 'custom', params: [], paramTypes: [] }
  ];

  // Common balance methods
  const commonBalanceMethods = [
    { label: 'balanceOf(address)', value: 'balanceOf', returnType: 'uint256' },
    { label: 'stakedBalanceOf(address)', value: 'stakedBalanceOf', returnType: 'uint256' },
    { label: 'userInfo(address)', value: 'userInfo', returnType: '(uint256,uint256)' },
    { label: 'getUserStakes(address)', value: 'getUserStakes', returnType: 'uint256' },
    { label: 'stakedAmount(address)', value: 'stakedAmount', returnType: 'uint256' },
    { label: 'deposits(address)', value: 'deposits', returnType: 'uint256' },
    { label: 'stakes(address,uint256)', value: 'stakes', returnType: '(uint256,uint256,uint256,uint256,bool)' },
    { label: 'Custom...', value: 'custom', returnType: 'uint256' }
  ];

  // Common balance return types
  const commonBalanceReturnTypes = [
    { label: 'uint256', value: 'uint256' },
    { label: '(uint256,uint256)', value: '(uint256,uint256)' },
    { label: '(uint256,uint256,uint256)', value: '(uint256,uint256,uint256)' },
    { label: '(uint256,uint256,uint256,uint256,bool)', value: '(uint256,uint256,uint256,uint256,bool)' },
    { label: '(uint256,uint256,uint256,uint256)', value: '(uint256,uint256,uint256,uint256)' },
    { label: '(uint256,uint256,bool)', value: '(uint256,uint256,bool)' },
    { label: 'uint128', value: 'uint128' },
    { label: 'uint64', value: 'uint64' },
    { label: 'uint32', value: 'uint32' },
    { label: 'Custom...', value: 'custom' }
  ];

  // 2. Add fetchSponsorBalances function
  const fetchSponsorBalances = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setSponsorBalances(data.balances || []);
    } catch (error) {
      setSponsorBalances([]);
    }
  };

  // 3. Call fetchSponsorBalances on page load
  useEffect(() => {
    loadData();
    loadRelayerStatus();
    fetchSponsorBalances();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load wallets
      const walletsResponse = await fetch(`${BACKEND_URL}/api/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (walletsResponse.ok) {
        const walletsData = await walletsResponse.json();
        setWallets(walletsData.success ? walletsData.wallets || [] : []);
      } else {
        console.error('Failed to load wallets:', walletsResponse.status);
        setWallets([]);
      }

      // Load delegations
      const delegationsResponse = await fetch(`${BACKEND_URL}/api/delegation/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (delegationsResponse.ok) {
        const delegationsData = await delegationsResponse.json();
        setDelegations(delegationsData.success ? delegationsData.delegations || [] : []);
      } else {
        console.error('Failed to load delegations:', delegationsResponse.status);
        setDelegations([]);
      }

      // Load staking contracts
      const contractsResponse = await fetch(`${BACKEND_URL}/api/delegation/staking-contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (contractsResponse.ok) {
        const contractsData = await contractsResponse.json();
        setStakingContracts(contractsData.success ? contractsData.stakingContracts || [] : []);
      } else {
        console.error('Failed to load staking contracts:', contractsResponse.status);
        setStakingContracts([]);
      }

      // Load actions
      const actionsResponse = await fetch(`${BACKEND_URL}/api/delegation/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (actionsResponse.ok) {
        const actionsData = await actionsResponse.json();
        setActions(actionsData.success ? actionsData.actions || [] : []);
      } else {
        console.error('Failed to load actions:', actionsResponse.status);
        setActions([]);
      }

      // Load sponsor balances
      await fetchSponsorBalances();
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadRelayerStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setRelayerStatus(data.status);
      }
    } catch (error) {
      console.error('Error loading relayer status:', error);
    }
  };



  const handleCreateDelegation = async () => {
    if (!delegationForm.walletId || !delegationForm.chainKey) {
      setError('Please fill in all required fields');
      return;
    }

    // Prevent double-clicking
    if (creatingDelegation) {
      return;
    }

    setCreatingDelegation(true);
    setError(''); // Clear previous errors
    setLoadingStep('initializing');
    setLoadingMessage('Starting delegation creation...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          walletId: delegationForm.walletId,
          chainKey: delegationForm.chainKey,
          recipientAddress: recipientAddress || null
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing delegation creation...');
      
      const data = await response.json();
      if (data.success) {
        let successMessage = 'Delegation created successfully!';
        if (data.txHash) {
          successMessage += ` Transaction: ${data.txHash}`;
        }
        if (data.warning) {
          successMessage += ` (Warning: ${data.warning})`;
        }
        setSuccess(successMessage);
        setCreateDelegationDialog(false);
        setDelegationForm({ walletId: '', chainKey: '' });
        loadData();
      } else {
        setError(data.error || 'Failed to create delegation');
      }
    } catch (error) {
      setError('Failed to create delegation');
      console.error('Error creating delegation:', error);
    } finally {
      setCreatingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleSetSponsor = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/sponsor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(sponsorForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Sponsor private key updated successfully');
        setSponsorDialog(false);
        setSponsorForm({ walletId: '', sponsorPrivateKey: '' });
        loadData();
      } else {
        setError(data.error || 'Failed to update sponsor');
      }
    } catch (error) {
      setError('Failed to update sponsor');
      console.error('Error setting sponsor:', error);
    }
  };

  const handleAddStakingContract = async () => {
    try {
      // Prepare the data with proper handling of custom fields
      const contractData = {
        ...stakingContractForm,
        balanceMethod: stakingContractForm.balanceMethod === 'custom' ? stakingContractForm.customBalanceMethod : stakingContractForm.balanceMethod,
        balanceReturnType: stakingContractForm.balanceReturnType === 'custom' ? stakingContractForm.customBalanceReturnType : stakingContractForm.balanceReturnType,
        unstakeMethod: stakingContractForm.unstakeMethod === 'custom' ? stakingContractForm.customUnstakeMethod : stakingContractForm.unstakeMethod,
        unstakeParamTypes: stakingContractForm.unstakeParamTypes || []
      };

      const response = await fetch(`${BACKEND_URL}/api/delegation/staking-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contractData)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Staking contract added successfully');
        setStakingContractDialog(false);
        setStakingContractForm({
          chainKey: 'ethereum',
          contractAddress: '',
          contractName: '',
          tokenAddress: '',
          rewardTokenAddress: '',
          balanceMethod: '',
          customBalanceMethod: '',
          balanceReturnType: 'uint256',
          customBalanceReturnType: '',

          unstakeMethod: '',
          customUnstakeMethod: '',
          unstakeParams: [], // Reset parameters on success
          unstakeParamTypes: [] // Reset parameter types on success
        });
        loadData();
      } else {
        setError(data.error || 'Failed to add staking contract');
      }
    } catch (error) {
      setError('Failed to add staking contract');
      console.error('Error adding staking contract:', error);
    }
  };

  const handleForceRevokeDelegation = async (walletId, chainKey) => {
    if (!window.confirm('Are you sure you want to force revoke this delegation? This will revoke the on-chain delegation even if it\'s not in our database.')) return;

    setRevokingDelegation(true);
    setError('');
    setLoadingStep('initializing');
    setLoadingMessage('Starting force revocation...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/force-revoke`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          walletId: walletId,
          chainKey: chainKey
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing force revocation...');
      
      const data = await response.json();
      if (data.success) {
        setSuccess(`Force revocation successful! Transaction: ${data.txHash}`);
        loadData();
      } else {
        setError(data.error || 'Failed to force revoke delegation');
      }
    } catch (error) {
      setError('Failed to force revoke delegation');
      console.error('Error force revoking delegation:', error);
    } finally {
      setRevokingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleRevokeDelegation = async (delegationId) => {
    if (!window.confirm('Are you sure you want to revoke this delegation? This will revoke both on-chain and database delegation.')) return;

    setRevokingDelegation(true);
    setError('');
    setLoadingStep('initializing');
    setLoadingMessage('Starting delegation revocation...');

    try {
      // Find the delegation to get wallet ID and chain key
      const delegation = delegations.find(d => d.id == delegationId);
      if (!delegation) {
        setError('Delegation not found');
        return;
      }

      // First try the POST endpoint (EIP-7702 revocation)
      const response = await fetch(`${BACKEND_URL}/api/delegation/revoke`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          walletId: delegation.wallet_id,
          chainKey: delegation.chain_key
        })
      });

      setLoadingStep('processing');
      setLoadingMessage('Processing delegation revocation...');
      
      const data = await response.json();
      if (data.success) {
        setSuccess(`Delegation revoked successfully! Transaction: ${data.txHash}`);
        loadData();
      } else {
        // Fallback to DELETE endpoint if POST fails
        setLoadingMessage('Falling back to database-only revocation...');
        const deleteResponse = await fetch(`${BACKEND_URL}/api/delegation/revoke/${delegationId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });

        const deleteData = await deleteResponse.json();
        if (deleteData.success) {
          setSuccess('Delegation revoked successfully (database only)');
          loadData();
        } else {
          setError(deleteData.error || 'Failed to revoke delegation');
        }
      }
    } catch (error) {
      setError('Failed to revoke delegation');
      console.error('Error revoking delegation:', error);
    } finally {
      setRevokingDelegation(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const handleRemoveStakingContract = async (contractId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/staking-contract/${contractId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Staking contract removed successfully');
        loadData();
      } else {
        setError(data.error || 'Failed to remove staking contract');
      }
    } catch (error) {
      setError('Failed to remove staking contract');
      console.error('Error removing staking contract:', error);
    }
  };

  const handleStartMonitoring = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/start-monitoring`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Monitoring started successfully');
        loadRelayerStatus();
      } else {
        setError(data.error || 'Failed to start monitoring');
      }
    } catch (error) {
      setError('Failed to start monitoring');
      console.error('Error starting monitoring:', error);
    }
  };

  const handleStopMonitoring = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/delegation/stop-monitoring`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Monitoring stopped successfully');
        loadRelayerStatus();
      } else {
        setError(data.error || 'Failed to stop monitoring');
      }
    } catch (error) {
      setError('Failed to stop monitoring');
      console.error('Error stopping monitoring:', error);
    }
  };

  const handleDirectUnstake = async () => {
    if (!directUnstakeForm.walletId || !directUnstakeForm.contractAddress || !directUnstakeForm.unstakeMethod) {
      setError('Please fill in all required fields');
      return;
    }

    setDirectUnstaking(true);
    setLoadingStep('Preparing unstaking transaction...');
    setError('');
    setSuccess('');

    try {
      setLoadingMessage('Creating EIP-7702 authorization...');
      
      const response = await fetch(`${BACKEND_URL}/api/delegation/direct-unstake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          walletId: directUnstakeForm.walletId,
          chainKey: directUnstakeForm.chainKey,
          contractAddress: directUnstakeForm.contractAddress,
          unstakeMethod: directUnstakeForm.unstakeMethod === 'custom' ? directUnstakeForm.customUnstakeMethod : directUnstakeForm.unstakeMethod,
          unstakeAmount: directUnstakeForm.unstakeAmount,
          unstakeParams: directUnstakeForm.unstakeParams,
          unstakeParamTypes: directUnstakeForm.unstakeParamTypes,
          parameterValues: directUnstakeForm.unstakeParamTypes, // Send the parameter values
          tokenAddress: directUnstakeForm.tokenAddress,
          recipientAddress: directUnstakeForm.recipientAddress
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`✅ Direct unstaking successful! Transaction Hash: ${data.txHash}`);
        setDirectUnstakeDialog(false);
        setDirectUnstakeForm({
          walletId: '',
          chainKey: 'ethereum',
          contractAddress: '',
          unstakeMethod: '',
          customUnstakeMethod: '',
          unstakeAmount: '',
          unstakeParams: [],
          unstakeParamTypes: [],
          tokenAddress: '',
          recipientAddress: ''
        });
        loadData();
      } else {
        setError(`❌ Direct unstaking failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setError(`❌ Failed to execute direct unstaking: ${error.message}`);
      console.error('Error executing direct unstaking:', error);
    } finally {
      setDirectUnstaking(false);
      setLoadingStep('');
      setLoadingMessage('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to check if a string is a valid Ethereum private key
  const isValidPrivateKey = (key) =>
    key === '' || (/^0x[a-fA-F0-9]{64}$/.test(key) && key.length === 66);
  
  // Helper function to derive sponsor address from private key
  const getSponsorAddress = (privateKey) => {
    if (!privateKey || privateKey === '') return null;
    try {
      const wallet = new ethers.Wallet(privateKey);
      return wallet.address;
    } catch (error) {
      return null;
    }
  };
  
  // Helper function to check if a string is a valid Ethereum address
  const isValidAddress = (address) => {
    return address === '' || ethers.isAddress(address);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Delegation Management
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDelegationDialog(true)}
            disabled={creatingDelegation}
            sx={{ mr: 1 }}
          >
            Create Delegation
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setStakingContractDialog(true)}
            sx={{ mr: 1 }}
          >
            Add Staking Contract
          </Button>
          <Button
            variant={relayerStatus.isRunning ? "outlined" : "contained"}
            color={relayerStatus.isRunning ? "error" : "success"}
            startIcon={relayerStatus.isRunning ? <StopIcon /> : <PlayArrowIcon />}
            onClick={relayerStatus.isRunning ? handleStopMonitoring : handleStartMonitoring}
          >
            {relayerStatus.isRunning ? 'Stop Monitoring' : 'Start Monitoring'}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Loading Message Display */}
      {(creatingDelegation || revokingDelegation) && loadingMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {loadingMessage}
            </Typography>
          </Box>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      {/* Relayer Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Relayer Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color={relayerStatus.isRunning ? 'success.main' : 'error.main'}>
                  {relayerStatus.isRunning ? '🟢' : '🔴'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {relayerStatus.isRunning ? 'Running' : 'Stopped'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{relayerStatus.monitoredWallets}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitored Wallets
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{(relayerStatus.providers || []).length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Chains
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4">{Array.isArray(relayerStatus.contracts) ? relayerStatus.contracts.length : relayerStatus.contracts ? Object.keys(relayerStatus.contracts).length : 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Deployed Contracts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sponsor Private Key Management Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sponsor Private Key Management
          </Typography>
          <TextField
            fullWidth
            label="Sponsor Private Key"
            type={showUserSponsorKey ? 'text' : 'password'}
            value={userSponsorKey}
            onChange={e => setUserSponsorKey(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowUserSponsorKey(v => !v)}
                  edge="end"
                >
                  {showUserSponsorKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            placeholder="0x..."
            helperText="This private key will be used to sponsor transactions"
          />
          {getSponsorAddress(userSponsorKey) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sponsor Address: <span style={{ fontFamily: 'monospace' }}>{getSponsorAddress(userSponsorKey)}</span>
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleSetUserSponsorKey}
            sx={{ mt: 1 }}
            disabled={!isValidPrivateKey(userSponsorKey)}
          >
            Save Sponsor Key
          </Button>
          {!isValidPrivateKey(userSponsorKey) && (
            <Typography color="error" variant="caption">
              Enter a valid 0x-prefixed 64-character private key or leave blank to remove.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Recipient Address Management Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recipient Address Management
          </Typography>
          <TextField
            fullWidth
            label="Recipient Address"
            type={showRecipientAddress ? 'text' : 'password'}
            value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowRecipientAddress(v => !v)}
                  edge="end"
                >
                  {showRecipientAddress ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            placeholder="0x..."
            helperText="Address to receive unstaked tokens. Leave empty to use sponsor address as fallback."
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSetRecipientAddress}
              disabled={!isValidAddress(recipientAddress)}
            >
              Save Recipient Address
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRemoveRecipientAddress}
              color="error"
            >
              Remove
            </Button>
          </Box>
          {!isValidAddress(recipientAddress) && recipientAddress !== '' && (
            <Typography color="error" variant="caption">
              Enter a valid Ethereum address or leave blank to remove.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Sponsor Key Base Token Balance Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sponsor Key Base Token Balances
          </Typography>
          <Button onClick={fetchSponsorBalances} size="small" sx={{ mb: 1 }}>
            Refresh Balances
          </Button>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Chain</TableCell>
                  <TableCell>Sponsor Address</TableCell>
                  <TableCell>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sponsorBalances && sponsorBalances.length > 0 ? sponsorBalances.map((row, idx) => (
                  <TableRow key={row.chain + row.sponsor}>
                    <TableCell>{row.chain}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace' }}>{row.sponsor}</TableCell>
                    <TableCell style={{ color: row.balance < 0.01 ? 'red' : undefined }}>
                      {row.balance} {row.symbol}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No sponsor balances found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Monitored Staking Contracts Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Monitored Staking Contracts ({Array.isArray(stakingContracts) ? stakingContracts.length : 0})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setStakingContractDialog(true)}
                size="small"
              >
                Add Contract
              </Button>
              <IconButton onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          {Array.isArray(stakingContracts) && stakingContracts.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chain</TableCell>
                    <TableCell>Contract Name</TableCell>
                    <TableCell>Contract Address</TableCell>
                    <TableCell>Token Address</TableCell>
                    <TableCell>Unstake Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stakingContracts.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell>
                        <Chip 
                          label={contract.chain_key} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {contract.contract_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatAddress(contract.contract_address)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {formatAddress(contract.token_address)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contract.unstake_method}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contract.is_active ? 'Active' : 'Inactive'} 
                          color={contract.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this staking contract?')) {
                              handleRemoveStakingContract(contract.id);
                            }
                          }}
                          title="Remove Contract"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              py={4}
              sx={{ 
                border: '2px dashed', 
                borderColor: 'divider', 
                borderRadius: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Staking Contracts
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                Add staking contracts to monitor for automated unstaking and delegation.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setStakingContractDialog(true)}
              >
                Add First Contract
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Direct Unstaking Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Direct Unstaking
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setDirectUnstakeDialog(true)}
                size="small"
              >
                Execute Unstake
              </Button>
              <IconButton onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            py={4}
            sx={{ 
              border: '2px dashed', 
              borderColor: 'divider', 
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Direct Unstaking
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
              Execute unstaking operations directly without relying on balance methods or automated monitoring.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDirectUnstakeDialog(true)}
            >
              Execute Unstake
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Delegated Wallets */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Delegated Wallets ({Array.isArray(wallets) ? wallets.filter(w => w.isDelegated).length : 0})
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => {
                      const orphanedWallets = wallets.filter(w => w.isDelegated && !delegations.find(d => 
        d.delegator_address.toLowerCase() === w.address.toLowerCase() && 
        d.is_active
      ));
                      if (orphanedWallets.length > 0) {
                        alert(`Found ${orphanedWallets.length} orphaned delegation(s):\n${orphanedWallets.map(w => `${w.name} (${w.address})`).join('\n')}`);
                      } else {
                        alert('No orphaned delegations found. All delegated wallets have database records.');
                      }
                    }}
                  >
                    Check Orphaned
                  </Button>
                  <IconButton onClick={loadData} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>
              
              {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                <Accordion key={wallet.id} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <AccountBalanceIcon sx={{ mr: 1 }} />
                      <Box flex={1}>
                        <Typography variant="subtitle1">{wallet.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatAddress(wallet.address)}
                        </Typography>
                      </Box>
                      <Chip 
                        label="Delegated" 
                        color="success" 
                        size="small" 
                        sx={{ mr: 1 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Address" 
                          secondary={wallet.address}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Delegated Chain" 
                          secondary={(() => {
                            // Check delegationSettings first for the specific delegated chain
                            if (wallet.delegationSettings) {
                              try {
                                const settings = typeof wallet.delegationSettings === 'string' 
                                  ? JSON.parse(wallet.delegationSettings) 
                                  : wallet.delegationSettings;
                                
                                if (settings.delegatedChain) {
                                  return settings.delegatedChain;
                                } else if (settings.chain) {
                                  return settings.chain;
                                }
                              } catch (e) {
                                // Fallback to parsing as string
                                if (wallet.delegationSettings.includes('delegatedChain')) {
                                  const match = wallet.delegationSettings.match(/"delegatedChain":"([^"]+)"/);
                                  if (match) return match[1];
                                }
                                if (wallet.delegationSettings.includes('chain')) {
                                  const match = wallet.delegationSettings.match(/"chain":"([^"]+)"/);
                                  if (match) return match[1];
                                }
                              }
                            }
                            
                            // Fallback: show all wallet chains if no delegation info found
                            let chains = wallet.chains;
                            if (Array.isArray(chains)) return chains.join(', ');
                            if (typeof chains === 'string') {
                              try {
                                const parsed = JSON.parse(chains);
                                if (Array.isArray(parsed)) return parsed.join(', ');
                              } catch {
                                // fallback: split by comma if not valid JSON
                                if (chains.includes(',')) return chains.split(',').map(s => s.trim().replace(/^"|"$/g, '')).join(', ');
                                return chains;
                              }
                            }
                            return '';
                          })()}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Status" 
                          secondary={wallet.status}
                        />
                      </ListItem>
                      <ListItem>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to revoke this delegation?')) {
                              // Find delegation by wallet address
                              const delegation = delegations.find(d => 
                                d.delegator_address.toLowerCase() === wallet.address.toLowerCase() && 
                                d.is_active
                              );
                              if (delegation) {
                                handleRevokeDelegation(delegation.id);
                              } else {
                                setError('No active delegation found for this wallet');
                              }
                            }
                          }}
                        >
                          Revoke Delegation
                        </Button>
                      </ListItem>
                      {wallet.isDelegated && !delegations.find(d => 
                        d.delegator_address.toLowerCase() === wallet.address.toLowerCase() && 
                        d.is_active
                      ) && (
                        <ListItem>
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => {
                              // Try to determine the chain from wallet chains or use a default
                              let chainKey = 'sepolia'; // default
                              if (wallet.chains) {
                                if (Array.isArray(wallet.chains)) {
                                  chainKey = wallet.chains[0];
                                } else if (typeof wallet.chains === 'string') {
                                  try {
                                    const parsed = JSON.parse(wallet.chains);
                                    chainKey = Array.isArray(parsed) ? parsed[0] : 'sepolia';
                                  } catch {
                                    chainKey = wallet.chains.split(',')[0]?.trim() || 'sepolia';
                                  }
                                }
                              }
                              handleForceRevokeDelegation(wallet.id, chainKey);
                            }}
                          >
                            Force Revoke (Orphaned)
                          </Button>
                        </ListItem>
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
              
              {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No delegated wallets found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Delegations */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Delegations ({Array.isArray(delegations) ? delegations.filter(d => d.is_active).length : 0})
              </Typography>
              
              {Array.isArray(delegations) && delegations.filter(d => d.is_active).map((delegation) => (
                <Box key={delegation.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      {delegation.wallet_name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Delegator: {formatAddress(delegation.delegator_address)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Delegate: {formatAddress(delegation.delegate_address)}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip label={delegation.chain_key} size="small" />
                    <Chip 
                      label={`Nonce: ${delegation.nonce}`} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
              ))}
              
              {Array.isArray(delegations) && delegations.filter(d => d.is_active).length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No active delegations
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Recent Delegated Actions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadData}
              >
                Refresh
              </Button>
            </Box>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Wallet</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Chain</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Transaction</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actions.slice(0, 10).map((action) => (
                      <TableRow key={action.id}>
                        <TableCell>{action.wallet_name}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {action.action_type === 'unstake' && <SwapHorizIcon fontSize="small" />}
                            {action.action_type === 'withdraw' && <SendIcon fontSize="small" />}
                            {action.action_type === 'transfer' && <SendIcon fontSize="small" />}
                            {action.action_type}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={action.chain_key} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={action.status} 
                            color={getStatusColor(action.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(action.created_at)}</TableCell>
                        <TableCell>
                          {action.transaction_hash ? (
                            <Typography variant="body2" fontFamily="monospace">
                              {formatAddress(action.transaction_hash)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Pending
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {Array.isArray(actions) && actions.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No actions found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Delegation Dialog */}
      <Dialog 
        open={createDelegationDialog} 
        onClose={() => !creatingDelegation && setCreateDelegationDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Create New Delegation
          {creatingDelegation && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage || 'Creating on-chain delegation...'}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={delegationForm.walletId}
                onChange={e => setDelegationForm(form => ({ ...form, walletId: e.target.value }))}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {Array.isArray(wallets) && wallets.filter(w => !w.isDelegated).map((wallet) => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Delegation will be created with the batcher contract as the delegate
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={delegationForm.chainKey}
                onChange={e => setDelegationForm(form => ({ ...form, chainKey: e.target.value }))}
                helperText="Note: Only one delegation per wallet per chain is allowed"
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDelegationDialog(false)} disabled={creatingDelegation}>Cancel</Button>
          <Button 
            onClick={handleCreateDelegation} 
            variant="contained" 
            disabled={creatingDelegation}
            startIcon={creatingDelegation ? <CircularProgress size={20} /> : null}
          >
            {creatingDelegation ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sponsor Dialog */}
      <Dialog open={sponsorDialog} onClose={() => setSponsorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Sponsor Private Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={sponsorForm.walletId}
                onChange={(e) => setSponsorForm({ ...sponsorForm, walletId: e.target.value })}
              >
                {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                  <option key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sponsor Private Key"
                type={showPrivateKey ? 'text' : 'password'}
                value={sponsorForm.sponsorPrivateKey}
                onChange={(e) => setSponsorForm({ ...sponsorForm, sponsorPrivateKey: e.target.value })}
                placeholder="0x..."
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                      edge="end"
                    >
                      {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
                helperText="This private key will be used to sponsor transactions"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSponsorDialog(false)}>Cancel</Button>
          <Button onClick={handleSetSponsor} variant="contained">Set Sponsor</Button>
        </DialogActions>
      </Dialog>

      {/* Staking Contract Dialog */}
      <Dialog open={stakingContractDialog} onClose={() => setStakingContractDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Staking Contract</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={stakingContractForm.chainKey}
                onChange={e => setStakingContractForm(form => ({
                  ...form,
                  chainKey: e.target.value
                }))}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Address"
                value={stakingContractForm.contractAddress}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, contractAddress: e.target.value })}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Name"
                value={stakingContractForm.contractName}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, contractName: e.target.value })}
                placeholder="e.g., Uniswap V3 Staking"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Token Address"
                value={stakingContractForm.tokenAddress}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, tokenAddress: e.target.value })}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reward Token Address (Optional)"
                value={stakingContractForm.rewardTokenAddress}
                onChange={(e) => setStakingContractForm({ ...stakingContractForm, rewardTokenAddress: e.target.value })}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Unstake Method"
                value={stakingContractForm.unstakeMethod || ''}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonUnstakeMethods.find(m => m.value === method);
                  // Extract the actual method name (before the underscore)
                  const actualMethod = method.includes('_') ? method.split('_')[0] : method;
                  setStakingContractForm(form => ({
                    ...form,
                    unstakeMethod: method,
                    unstakeParams: found ? found.params : [],
                    unstakeParamTypes: found ? found.paramTypes : [],
                    customUnstakeMethod: method === 'custom' ? '' : actualMethod
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonUnstakeMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.unstakeMethod === 'custom' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Custom Unstake Method"
                    value={stakingContractForm.customUnstakeMethod}
                    onChange={e => setStakingContractForm(form => ({ ...form, customUnstakeMethod: e.target.value }))}
                    placeholder="e.g., claimRewards"
                  />
                </Grid>
                {/* Dynamic parameter fields for custom method */}
                {stakingContractForm.unstakeParams && stakingContractForm.unstakeParams.map((param, idx) => (
                  <Box key={`custom-param-${idx}`} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      select
                      label={`Param ${idx + 1} Type`}
                      value={param}
                      onChange={e => {
                        const newParams = [...stakingContractForm.unstakeParams];
                        newParams[idx] = e.target.value;
                        setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                      }}
                      sx={{ minWidth: 150, flexGrow: 1 }}
                    >
                      <MenuItem value="">No Parameter</MenuItem>
                      <MenuItem value="uint256">uint256</MenuItem>
                      <MenuItem value="address">address</MenuItem>
                      <MenuItem value="bool">bool</MenuItem>
                      <MenuItem value="bytes">bytes</MenuItem>
                      <MenuItem value="string">string</MenuItem>
                      <MenuItem value="uint128">uint128</MenuItem>
                      <MenuItem value="uint64">uint64</MenuItem>
                      <MenuItem value="uint32">uint32</MenuItem>
                    </TextField>
                    <IconButton onClick={() => {
                      const newParams = stakingContractForm.unstakeParams.filter((_, i) => i !== idx);
                      setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setStakingContractForm(form => ({
                    ...form,
                    unstakeParams: [...(form.unstakeParams || []), 'uint256']
                  }))}
                  sx={{ mt: 1 }}
                >
                  Add Parameter
                </Button>
              </>
            )}
            {stakingContractForm.unstakeMethod !== 'custom' && stakingContractForm.unstakeParams.length > 0 && (
              <>
                {/* Parameter fields for common methods with params */}
                {stakingContractForm.unstakeParams.map((param, idx) => (
                  <Box key={`common-param-${idx}`} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      select
                      label={`Param ${idx + 1} Type`}
                      value={param}
                      onChange={e => {
                        const newParams = [...stakingContractForm.unstakeParams];
                        newParams[idx] = e.target.value;
                        setStakingContractForm(form => ({ ...form, unstakeParams: newParams }));
                      }}
                      sx={{ minWidth: 150, flexGrow: 1 }}
                    >
                      <MenuItem value="">No Parameter</MenuItem>
                      <MenuItem value="uint256">uint256</MenuItem>
                      <MenuItem value="address">address</MenuItem>
                      <MenuItem value="bool">bool</MenuItem>
                      <MenuItem value="bytes">bytes</MenuItem>
                      <MenuItem value="string">string</MenuItem>
                      <MenuItem value="uint128">uint128</MenuItem>
                      <MenuItem value="uint64">uint64</MenuItem>
                      <MenuItem value="uint32">uint32</MenuItem>
                    </TextField>
                  </Box>
                ))}
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Balance Method"
                value={stakingContractForm.balanceMethod || ''}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonBalanceMethods.find(m => m.value === method);
                  setStakingContractForm(form => ({
                    ...form,
                    balanceMethod: method,
                    balanceReturnType: found ? found.returnType : form.balanceReturnType,
                    customBalanceMethod: method === 'custom' ? '' : method
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonBalanceMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.balanceMethod === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Balance Method"
                  value={stakingContractForm.customBalanceMethod}
                  onChange={e => setStakingContractForm(form => ({ ...form, customBalanceMethod: e.target.value }))}
                  placeholder="e.g., getUserStakes"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Balance Return Type"
                value={stakingContractForm.balanceReturnType || ''}
                onChange={e => {
                  const returnType = e.target.value;
                  setStakingContractForm(form => ({
                    ...form,
                    balanceReturnType: returnType,
                    customBalanceReturnType: returnType === 'custom' ? '' : returnType
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonBalanceReturnTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {stakingContractForm.balanceReturnType === 'custom' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Balance Return Type"
                  value={stakingContractForm.customBalanceReturnType}
                  onChange={e => setStakingContractForm(form => ({ ...form, customBalanceReturnType: e.target.value }))}
                  placeholder="e.g., uint256"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStakingContractDialog(false)}>Cancel</Button>
          <Button onClick={handleAddStakingContract} variant="contained">Add Contract</Button>
        </DialogActions>
      </Dialog>

      {/* Direct Unstaking Dialog */}
      <Dialog open={directUnstakeDialog} onClose={() => !directUnstaking && setDirectUnstakeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Execute Direct Unstaking
          {directUnstaking && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage || 'Executing unstaking transaction...'}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {directUnstaking && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                <strong>Status:</strong> {loadingMessage || 'Executing unstaking transaction...'}
              </Typography>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Wallet"
                value={directUnstakeForm.walletId}
                onChange={e => setDirectUnstakeForm(form => ({ ...form, walletId: e.target.value }))}
                disabled={directUnstaking}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {Array.isArray(wallets) && wallets.filter(w => w.isDelegated).map((wallet) => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    {wallet.name} ({formatAddress(wallet.address)})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Chain"
                value={directUnstakeForm.chainKey}
                onChange={e => setDirectUnstakeForm(form => ({ ...form, chainKey: e.target.value }))}
                disabled={directUnstaking}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 300
                      }
                    }
                  }
                }}
              >
                <MenuItem value="ethereum">Ethereum</MenuItem>
                <MenuItem value="sepolia">Sepolia</MenuItem>
                <MenuItem value="bsc">BSC</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Address"
                value={directUnstakeForm.contractAddress}
                onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, contractAddress: e.target.value })}
                placeholder="0x..."
                disabled={directUnstaking}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Unstake Method"
                value={directUnstakeForm.unstakeMethod || ''}
                disabled={directUnstaking}
                onChange={e => {
                  const method = e.target.value;
                  const found = commonUnstakeMethods.find(m => m.value === method);
                  const actualMethod = method.includes('_') ? method.split('_')[0] : method;
                  setDirectUnstakeForm(form => ({
                    ...form,
                    unstakeMethod: method,
                    unstakeParams: found ? found.params : [],
                    unstakeParamTypes: found ? found.paramTypes : [],
                    customUnstakeMethod: method === 'custom' ? '' : actualMethod
                  }));
                }}
                sx={{ minWidth: '100%' }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 400
                      }
                    }
                  }
                }}
              >
                {commonUnstakeMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {directUnstakeForm.unstakeMethod === 'custom' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Custom Unstake Method"
                    value={directUnstakeForm.customUnstakeMethod}
                    onChange={e => setDirectUnstakeForm(form => ({ ...form, customUnstakeMethod: e.target.value }))}
                    placeholder="e.g., claimRewards"
                    disabled={directUnstaking}
                  />
                </Grid>
                {/* Dynamic parameter fields for custom method */}
                {directUnstakeForm.unstakeParams && directUnstakeForm.unstakeParams.map((param, idx) => (
                  <Box key={`custom-param-${idx}`} display="flex" flexDirection="column" gap={1} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        label={`Param ${idx + 1} Type`}
                        value={param}
                        onChange={e => {
                          const newParams = [...directUnstakeForm.unstakeParams];
                          newParams[idx] = e.target.value;
                          setDirectUnstakeForm(form => ({ ...form, unstakeParams: newParams }));
                        }}
                        sx={{ minWidth: 150, flexGrow: 1 }}
                      >
                        <MenuItem value="">No Parameter</MenuItem>
                        <MenuItem value="uint256">uint256</MenuItem>
                        <MenuItem value="address">address</MenuItem>
                        <MenuItem value="bool">bool</MenuItem>
                        <MenuItem value="bytes">bytes</MenuItem>
                        <MenuItem value="string">string</MenuItem>
                        <MenuItem value="uint128">uint128</MenuItem>
                        <MenuItem value="uint64">uint64</MenuItem>
                        <MenuItem value="uint32">uint32</MenuItem>
                      </TextField>
                      <IconButton onClick={() => {
                        const newParams = directUnstakeForm.unstakeParams.filter((_, i) => i !== idx);
                        const newParamTypes = directUnstakeForm.unstakeParamTypes.filter((_, i) => i !== idx);
                        setDirectUnstakeForm(form => ({ 
                          ...form, 
                          unstakeParams: newParams,
                          unstakeParamTypes: newParamTypes
                        }));
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      label={`Parameter ${idx + 1} Value`}
                      value={directUnstakeForm.unstakeParamTypes[idx] || ''}
                      onChange={e => {
                        const newParamTypes = [...directUnstakeForm.unstakeParamTypes];
                        newParamTypes[idx] = e.target.value;
                        setDirectUnstakeForm(form => ({ ...form, unstakeParamTypes: newParamTypes }));
                      }}
                      placeholder={`Enter ${param} value`}
                      helperText={`Parameter type: ${param}`}
                      disabled={directUnstaking}
                    />
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDirectUnstakeForm(form => ({
                    ...form,
                    unstakeParams: [...(form.unstakeParams || []), 'uint256'],
                    unstakeParamTypes: [...(form.unstakeParamTypes || []), '']
                  }))}
                  disabled={directUnstaking}
                  sx={{ mt: 1 }}
                >
                  Add Parameter
                </Button>
              </>
            )}
            
            {/* Parameter value fields for non-custom methods */}
            {directUnstakeForm.unstakeMethod !== 'custom' && directUnstakeForm.unstakeParams && directUnstakeForm.unstakeParams.map((param, idx) => (
              <Grid item xs={12} key={`unstake-param-${idx}`}>
                <TextField
                  fullWidth
                  label={`Parameter ${idx + 1} Value`}
                  value={directUnstakeForm.unstakeParamTypes[idx] || ''}
                  onChange={e => {
                    const newParamTypes = [...directUnstakeForm.unstakeParamTypes];
                    newParamTypes[idx] = e.target.value;
                    setDirectUnstakeForm(form => ({ ...form, unstakeParamTypes: newParamTypes }));
                  }}
                  placeholder={`Enter ${param} value`}
                  helperText={`Parameter type: ${param}${directUnstakeForm.unstakeParamTypes[idx] ? ` (${directUnstakeForm.unstakeParamTypes[idx]})` : ''}`}
                  disabled={directUnstaking}
                />
              </Grid>
            ))}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Unstake Amount (Optional)"
                value={directUnstakeForm.unstakeAmount}
                onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, unstakeAmount: e.target.value })}
                placeholder="e.g., 1000000000000000000 (in wei)"
                helperText="Leave empty for full balance or when not needed"
                disabled={directUnstaking}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Token Address (Optional)"
                value={directUnstakeForm.tokenAddress}
                onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, tokenAddress: e.target.value })}
                placeholder="0x..."
                helperText="Token contract address for automatic transfer after unstaking"
                disabled={directUnstaking}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recipient Address (Optional)"
                value={directUnstakeForm.recipientAddress}
                onChange={(e) => setDirectUnstakeForm({ ...directUnstakeForm, recipientAddress: e.target.value })}
                placeholder="0x..."
                helperText="Address to receive tokens after unstaking (required if token address is provided)"
                disabled={directUnstaking}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDirectUnstakeDialog(false)}
            disabled={directUnstaking}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDirectUnstake} 
            variant="contained"
            disabled={directUnstaking}
            startIcon={directUnstaking ? <CircularProgress size={16} /> : null}
          >
            {directUnstaking ? 'Executing...' : 'Execute Unstake'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Delegation;